import { useRef, useEffect } from 'react'

/**
 * A hook that enables smooth horizontal scrolling with the vertical mouse wheel
 * @param scrollAmount - The base amount to scroll horizontally per wheel tick (default: 50)
 * @param smoothness - How smooth the scrolling should be (lower = smoother, default: 8)
 * @returns A ref to attach to the scrollable element
 */
export function useHorizontalScroll<T extends HTMLElement>(scrollAmount = 50, smoothness = 8) {
  const scrollRef = useRef<T | null>(null)
  // Track animation frame to cancel it when needed
  const animationRef = useRef<number | null>(null)
  // Track target scroll position
  const targetScrollRef = useRef<number | null>(null)

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return

      // Prevent the default vertical scroll
      e.preventDefault()

      // Calculate target scroll position
      const direction = e.deltaY > 0 ? 1 : -1
      const scrollMagnitude = (Math.min(Math.abs(e.deltaY), 100) / 100) * scrollAmount

      // Set or update the target scroll position
      if (targetScrollRef.current === null) {
        targetScrollRef.current = element.scrollLeft
      }

      targetScrollRef.current += direction * scrollMagnitude

      // Clamp the target scroll position to the maximum scroll position
      targetScrollRef.current = Math.max(
        0,
        Math.min(targetScrollRef.current, element.scrollWidth - element.clientWidth),
      )

      // Start smooth scrolling animation if not already running
      if (animationRef.current === null) {
        const animateScroll = () => {
          if (!element || targetScrollRef.current === null) return

          const diff = targetScrollRef.current - element.scrollLeft

          // If we're close enough to the target, stop animating
          if (Math.abs(diff) < 3) {
            element.scrollLeft = targetScrollRef.current
            targetScrollRef.current = null
            animationRef.current = null
            return
          }

          // Move a fraction of the way to the target (smoothness factor)
          element.scrollLeft += diff / smoothness

          // Continue animation
          animationRef.current = requestAnimationFrame(animateScroll)
        }

        animationRef.current = requestAnimationFrame(animateScroll)
      }
    }

    element.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      element.removeEventListener('wheel', handleWheel)
      // Cancel any ongoing animation when unmounting
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [scrollAmount, smoothness])

  return scrollRef
}
